import {
  TextField,
  TextFieldProps,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { CustomFormLabel } from "./CustomFormLabel";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { debounce } from "lodash";
import { Close } from "@mui/icons-material";

export const CustomTextField = styled((props: TextFieldProps) => (
  <TextField {...props} />
))(({ theme }) => ({
  "& input[type=number]": {
    "-moz-appearance": "textfield",
  },

  /* ---------------- LABEL COLORS ---------------- */
  "& .MuiInputLabel-root": {
    fontSize: "0.8rem",
    marginTop: "-0.4rem",
    color: "#757575",
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: "#5a5a5a",
  },
  "& .MuiInputLabel-root.Mui-error": {
    color: "#dc2626",
  },
  "& .MuiInputLabel-root.Mui-disabled": {
    color: "#757575",
  },
  "& .MuiInputLabel-shrink": {
    fontSize: "0.85rem",
    fontWeight: 600,
    marginTop: "0.1rem",
    color: "#5a5a5a",
  },

  /* ---------------- NUMBER INPUT FIX ---------------- */

  "& input[type=number]::-webkit-outer-spin-button": {
    "-webkit-appearance": "none",
    margin: 0,
  },
  "& input[type=number]::-webkit-inner-spin-button": {
    "-webkit-appearance": "none",
    margin: 0,
  },

  /* ---------------- INPUT ---------------- */

  "& .MuiOutlinedInput-input": {
    padding: "9px 13px",
    fontSize: "0.8rem",
    color: "#454545",
  },

  "& .MuiInputBase-root.MuiOutlinedInput-root.MuiInputBase-multiline": {
    padding: "0px",
  },

  "& .MuiInputBase-input.MuiOutlinedInput-input.MuiInputBase-inputMultiline": {
    padding: "9px 13px",
    fontSize: "0.8rem",
    color: "#454545",
  },

  /* ---------------- PLACEHOLDER ---------------- */

  "& .MuiOutlinedInput-input::placeholder": {
    color: "#767e89",
    opacity: 1,
  },

  /* ---------------- BORDER ---------------- */

  "& .MuiOutlinedInput-notchedOutline": {
    borderColor:
      theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.12)" : "#dee3e9",
    borderRadius: "5px",
  },

  /* ---------------- DISABLED ---------------- */

  "& .MuiOutlinedInput-input.Mui-disabled": {
    backgroundColor:
      theme.palette.mode === "dark" ? "rgba(0, 0, 0, 0.12)" : "#f8f9fb",
    fontWeight: 600,
    WebkitTextFillColor: "rgba(0, 0, 0, 0.55)",
  },

  "& .MuiOutlinedInput-input.Mui-disabled::placeholder": {
    color: "#383838ff",
    opacity: 1,
  },
}));

export const CustomShrinkTextField = styled((props: TextFieldProps) => (
  <TextField {...props} />
))(({ theme }) => ({
  "& input[type=number]": {
    "-moz-appearance": "textfield",
  },
  "& input[type=number]::-webkit-outer-spin-button": {
    "-webkit-appearance": "none",
    margin: 0,
  },
  "& input[type=number]::-webkit-inner-spin-button": {
    "-webkit-appearance": "none",
    margin: 0,
  },
  "& .MuiInputLabel-shrink": {
    fontSize: "1.3rem",
    fontWeight: 600, // Increase the font size here
  },

  "& .MuiInputLabel-shrink + .MuiOutlinedInput-root": {
    "& fieldset": {
      legend: {
        maxWidth: "100%", // Ensure it does not overflow
        fontSize: "1.05rem",
      },
    },
  },
}));

export const CustomShrinkTextFieldSm = styled((props: TextFieldProps) => (
  <TextField
    {...props}
    variant="outlined"
    InputLabelProps={{ shrink: true }} // 👈 Always shrink
  />
))(({ theme }) => ({
  "& input[type=number]": {
    MozAppearance: "textfield",
  },
  "& input[type=number]::-webkit-outer-spin-button": {
    WebkitAppearance: "none",
    margin: 0,
  },
  "& input[type=number]::-webkit-inner-spin-button": {
    WebkitAppearance: "none",
    margin: 0,
  },

  "& .MuiOutlinedInput-input": {
    padding: "9px 13px",
    fontSize: "0.8rem",
    color: "rgb(38, 38, 38)",
  },

  /* Shrunk label style */
  "& .MuiInputLabel-shrink": {
    fontSize: "0.85rem",
    fontWeight: 600,
  },

  /* Keep notch clean – DO NOT touch width */
  "& .MuiOutlinedInput-notchedOutline legend": {
    padding: "0 6px",
  },
}));

interface CustomNumberFieldProps extends Omit<TextFieldProps, "value"> {
  readOnlyMode?: boolean;
  value?: string | number;
  heading?: string;
}

export const CustomNumberField: React.FC<CustomNumberFieldProps> = (props) => {
  const { value, onChange, heading, ...rest } = props;

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    let newValue = event.target.value;

    newValue = newValue.replace(/[^0-9.]/g, "");

    if (newValue.length > 1) {
      newValue = newValue.replace(/^0+(?=\d)/, "");
    }

    if (onChange) {
      onChange({
        ...event,
        target: { ...event.target, value: newValue },
      });
    }
  };

  const processedValue = useMemo(() => {
    if (value === undefined || value === null) return "";
    const stringValue = value.toString();

    if (stringValue === "0") return "0";

    return stringValue.replace(/^0+(?=\d)/, "");
  }, [value]);

  return (
    <>
      {heading && <CustomFormLabel>{heading}</CustomFormLabel>}
      <CustomTextField
        {...rest}
        fullWidth
        value={processedValue}
        onChange={handleChange}
        InputProps={{
          inputProps: {
            min: 0,
            inputMode: "numeric",
            pattern: "[0-9]*",
          },
        }}
      />
    </>
  );
};

interface CustomPasswordFieldProps extends Omit<TextFieldProps, "value"> {
  readOnlyMode?: boolean;
  value?: string | number;
  heading?: string;
  showPassword?: boolean;
  onShowPasswordChange?: (show: boolean) => void;
}

export const CustomPasswordField: React.FC<CustomPasswordFieldProps> = (
  props
) => {
  const {
    value,
    onChange,
    heading,
    showPassword: externalShowPassword,
    onShowPasswordChange,
    ...rest
  } = props;
  const [internalShowPassword, setInternalShowPassword] = React.useState(false);

  // Use external showPassword if provided, otherwise use internal state
  const showPassword =
    externalShowPassword !== undefined
      ? externalShowPassword
      : internalShowPassword;

  const handleClickShowPassword = () => {
    const newShowPassword = !showPassword;
    if (onShowPasswordChange) {
      onShowPasswordChange(newShowPassword);
    } else {
      setInternalShowPassword(newShowPassword);
    }
  };

  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
  };

  return (
    <>
      {heading && <CustomFormLabel>{heading}</CustomFormLabel>}
      <CustomTextField
        {...rest}
        value={value}
        onChange={onChange}
        type={showPassword ? "text" : "password"}
        InputProps={{
          ...rest.InputProps,
          endAdornment: (
            <IconButton
              aria-label="toggle password visibility"
              onClick={handleClickShowPassword}
              onMouseDown={handleMouseDownPassword}
              edge="end"
              sx={{
                color: showPassword ? "primary.main" : "grey.500",
                "&:hover": {
                  color: showPassword ? "primary.dark" : "grey.700",
                },
              }}
            >
              {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
            </IconButton>
          ),
        }}
      />
    </>
  );
};

interface DebouncedTextFieldProps {
  value?: string;
  delay?: number;
  onChange: (value: string) => void; // behaves like normal onChange
  InputProps?: any;
  disabled?: boolean;
  [key: string]: any;
}

export const DebouncedTextField: React.FC<DebouncedTextFieldProps> = ({
  value = "",
  delay = 600,
  onChange,
  InputProps,
  disabled,
  ...props
}) => {
  const [localValue, setLocalValue] = useState(value);
  const isTypingRef = useRef(false);

  // Debounced parent update (API trigger)
  const debouncedChange = useMemo(() => {
    return debounce((val: string) => {
      isTypingRef.current = false;
      onChange(val);
    }, delay);
  }, [delay, onChange]);

  useEffect(() => {
    return () => {
      debouncedChange.cancel();
    };
  }, [debouncedChange]);

  // Only accept external value when NOT typing
  useEffect(() => {
    if (!isTypingRef.current) {
      setLocalValue(value || "");
    }
  }, [value]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    isTypingRef.current = true;
    setLocalValue(val);
    debouncedChange(val);
  };

  const handleClear = () => {
    isTypingRef.current = false;
    setLocalValue("");
    debouncedChange.cancel();
    onChange("");
  };

  return (
    <CustomTextField
      {...props}
      disabled={disabled}
      sx={{
        ...props.sx,
        ...(localValue.length > 0
          ? {
              backgroundColor: "#fff2c7",
              borderRadius: 1,

              // default
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "warning.main",
                borderWidth: "2px",
              },

              // hover (this overrides MUI)
              "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline":
                {
                  borderColor: "warning.main",
                },

              // focus (this overrides MUI)
              "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                {
                  borderColor: "warning.main",
                  borderWidth: "2px",
                },
            }
          : {}),
      }}
      value={localValue}
      onChange={handleInputChange}
      InputProps={{
        endAdornment: localValue && (
          <InputAdornment position="end">
            <IconButton size="small" onClick={handleClear}>
              <Close fontSize="small" />
            </IconButton>
          </InputAdornment>
        ),
      }}
    />
  );
};
